import React, { useMemo } from 'react';
import { MdCleaningServices } from 'react-icons/md';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import {
    Checkbox,
    Chip,
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core';

import CardSideBordered from '~/components/CardSideBordered';
import InfoCard from '~/components/InfoCard';
import LocaleMessage from '~/components/LocaleMessage';
import ExportCSVButton from '~/components/Reports/CSVButton';
import ReportHeader from '~/components/Reports/ReportHeader';

import GetFileName from '~/util/GetFileName';

import CleaningReportAreaByHourCard from '../CleaningReportsDashboard/CleaningReportAreaByHourCard';
import CleaningReportAreaComparisonCard from '../CleaningReportsDashboard/CleaningReportAreaComparisonCard';
import CleaningReportBatteryByModeCard from '../CleaningReportsDashboard/CleaningReportBatteryByModeCard';
import CleaningReportBatteryDashboard from '../CleaningReportsDashboard/CleaningReportBatteryDashboard';
import CleaningReportChartsCard from '../CleaningReportsDashboard/CleaningReportChartsCard';
// import CleaningReportFleetScheduleCard from '../CleaningReportsDashboard/CleaningReportFleetScheduleCard';
import CleaningReportProductSpentCard from '../CleaningReportsDashboard/CleaningReportProductSpentCard';
import CleaningReportROICard from '../CleaningReportsDashboard/CleaningReportROICard';
import CleaningReportTasksTablePagination from '../CleaningReportsDashboard/CleaningReportTasksTablePagination';
import CleaningReportUtilityCharts from '../CleaningReportsDashboard/CleaningReportUtilityCharts';
import CleaningReportUtilityInfoCards from '../CleaningReportsDashboard/CleaningReportUtilityInfoCards';
import {
    getCleaningReportCsvHeaders,
    buildCleaningReportCsvData,
} from '../CleaningReportsDashboard/cleaningReportUtils';
import {
    buildGroupComparisonDataFromList,
    formatGroupComparisonData,
} from '../CleaningReportsDashboard/GroupComparisonDashboardLogic';
import {
    buildRobotComparisonDataFromList,
    mergeConsumptionIntoRobotComparison,
} from '../CleaningReportsDashboard/RobotComparisonDashboardLogic';
import CleaningTaskTableHead from './CleaningTaskTableHead';
import CleaningTaskTableRows from './CleaningTaskTableRows';
import { CC1_ROBOT_FILTER_SELECT_ALL_VALUE } from './constants';
import EfficiencyGraphCard from './Efficiency/EfficiencyGraphCard';
import EfficiencyGroupComparisonCard from './Efficiency/EfficiencyGroupComparisonCard';
import EfficiencyInfoCard from './Efficiency/EfficiencyInfoCard';
import EfficiencyRobotComparisonCard from './Efficiency/EfficiencyRobotComparisonCard';
// import GroupComparisonDashboardCard from '../CleaningReportsDashboard/GroupComparisonDashboardCard';
// import RobotComparisonDashboardCard from '../CleaningReportsDashboard/RobotComparisonDashboardCard';

const cc1RobotPropType = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    code: PropTypes.string,
});

const dateFnsLocalePropType = PropTypes.shape({
    lang: PropTypes.string.isRequired,
});

const shortcutListPropType = PropTypes.shape({
    today: PropTypes.node.isRequired,
    '30_days': PropTypes.node.isRequired,
    '60_days': PropTypes.node.isRequired,
    '90_days': PropTypes.node.isRequired,
    custom: PropTypes.node.isRequired,
});

const areaComparePointPropType = PropTypes.shape({
    date: PropTypes.string.isRequired,
    clean_area: PropTypes.number.isRequired,
    task_area: PropTypes.number.isRequired,
});

const areaByHourPointPropType = PropTypes.shape({
    hour: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    clean_area: PropTypes.number.isRequired,
});

const cleaningReportRowPropType = PropTypes.shape({
    create_time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    clean_time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    clean_area: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    task_area: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    report_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

const domRefCurrentPropType =
    typeof HTMLElement !== 'undefined'
        ? PropTypes.oneOfType([
              PropTypes.instanceOf(HTMLElement),
              PropTypes.oneOf([null]),
          ])
        : PropTypes.oneOf([null]);

const cleaningTableRefPropType = PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
        current: domRefCurrentPropType,
    }),
]);

function CleaningReportPluginspaceMainCard({
    cc1Robots,
    cc1RobotsLoading,
    selectedCc1RobotIds,
    onCc1RobotFilterChange,
    onCc1RobotFilterConfirm,
    date_loc,
    draftPeriod,
    handleCleaningFromDateChange,
    handleCleaningUntilDateChange,
    applyPeriod,
    currShortcut,
    shortcut_list,
    handleCleaningPeriodShortcut,
    total,
    totalCleanFormatted,
    totalArea,
    robotsQueried,
    cardColor,
    graphColor,
    pieSegmentColors,
    list,
    localeCode,
    periodDates,
    areaCompareSeries,
    areaByHourSeries,
    scrollToCleaningTable,
    cleaningTableRef,
    tableRowsForDisplay,
    onRowClick,
    tasksTablePage,
    tasksTableRowsPerPage,
    tasksTableTotalCount,
    onTasksTablePageChange,
    onTasksTableRowsPerPageChange,
    intl,
    humanProd,
    reportPayload,
    loadError,
    robotDilutions,
}) {
    const user = useSelector(state => state.user);
    const settings = useSelector(state => state.settings);
    const profile_permission =
        user && user.profile ? user.profile.permission : null;
    const isAdmin = ['master', 'super_admin', 'admin'].includes(
        profile_permission
    );

    let saasGroups = [];
    if (isAdmin && settings) {
        saasGroups = settings.admin_groups;
    } else if (user && user.groups) {
        saasGroups = user.groups;
    }

    const isAllGroupsSelected =
        !settings ||
        !settings.active ||
        !settings.active.id ||
        settings.active.id === '*';

    const groupComparisonData = formatGroupComparisonData(
        buildGroupComparisonDataFromList(list, cc1Robots, saasGroups, intl),
        intl
    );

    const robotComparisonData = useMemo(() => {
        const selectedRobots = cc1Robots.filter(
            r => r && r.id && selectedCc1RobotIds.includes(r.id)
        );
        const base = buildRobotComparisonDataFromList(
            list,
            selectedRobots,
            intl
        );
        return mergeConsumptionIntoRobotComparison(
            base,
            reportPayload && reportPayload.consumption
        );
    }, [list, cc1Robots, selectedCc1RobotIds, intl, reportPayload]);

    const allCc1Ids = cc1Robots.map(r => r && r.id).filter(Boolean);
    const allCc1Selected =
        allCc1Ids.length > 0 &&
        allCc1Ids.every(id => selectedCc1RobotIds.includes(id));
    const cc1SelectIndeterminate =
        selectedCc1RobotIds.length > 0 && !allCc1Selected;

    const csvHeaders = useMemo(() => {
        return getCleaningReportCsvHeaders(intl, true);
    }, [intl]);

    const csvData = useMemo(() => {
        return buildCleaningReportCsvData(list, localeCode, true);
    }, [list, localeCode]);
    const displayRobotsQueried =
        (reportPayload && reportPayload.robots_queried) || robotsQueried;

    return (
        <div className="col-12 mb-5 mt-3">
            <CardSideBordered
                title={
                    <LocaleMessage msg="page.robots.cleaning_report.title" />
                }
                Icon={MdCleaningServices}
                hide
            >
                <div>
                    <TextField
                        select
                        variant="outlined"
                        fullWidth
                        className="mb-3"
                        disabled={cc1Robots.length === 0 || cc1RobotsLoading}
                        label={
                            <LocaleMessage msg="page.robots.cleaning_report.live.filter_cc1_robots" />
                        }
                        value={selectedCc1RobotIds}
                        onChange={onCc1RobotFilterChange}
                        SelectProps={{
                            multiple: true,
                            onClose: onCc1RobotFilterConfirm,
                            MenuProps: {
                                getContentAnchorEl: null,
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                            },
                            renderValue: selected => {
                                if (!selected || selected.length === 0) {
                                    return (
                                        <Typography
                                            component="span"
                                            color="textSecondary"
                                        >
                                            <LocaleMessage msg="page.robots.cleaning_report.live.filter_cc1_none" />
                                        </Typography>
                                    );
                                }
                                return (
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'nowrap',
                                            overflow: 'hidden',
                                            gap: 6,
                                        }}
                                    >
                                        {selected.map(id => {
                                            const r = cc1Robots.find(
                                                x => x.id === id
                                            );
                                            const chipLabel = r
                                                ? `${r.name || ''}${
                                                      r.code
                                                          ? ` (${r.code})`
                                                          : ''
                                                  }`.trim() || id
                                                : id;
                                            return (
                                                <Chip
                                                    key={id}
                                                    size="small"
                                                    label={chipLabel}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            },
                        }}
                    >
                        <MenuItem
                            dense
                            value={CC1_ROBOT_FILTER_SELECT_ALL_VALUE}
                        >
                            <Checkbox
                                color="primary"
                                size="small"
                                checked={allCc1Selected}
                                indeterminate={cc1SelectIndeterminate}
                                tabIndex={-1}
                                disableRipple
                                style={{ marginRight: 8, padding: 4 }}
                            />
                            <LocaleMessage
                                msg={
                                    allCc1Selected
                                        ? 'label.deselect_all'
                                        : 'label.select_all'
                                }
                            />
                        </MenuItem>
                        {cc1Robots.map(r => (
                            <MenuItem key={r.id} value={r.id} dense>
                                <Checkbox
                                    color="primary"
                                    size="small"
                                    checked={selectedCc1RobotIds.includes(r.id)}
                                    tabIndex={-1}
                                    disableRipple
                                    style={{ marginRight: 8, padding: 4 }}
                                />
                                {r.name
                                    ? `${r.name}${r.code ? ` (${r.code})` : ''}`
                                    : r.code || r.id}
                            </MenuItem>
                        ))}
                    </TextField>
                    <ReportHeader
                        date_props={{
                            locale: date_loc,
                        }}
                        period={{
                            minDate: null,
                            fromDate: new Date(
                                `${draftPeriod.startDate}T12:00:00`
                            ),
                            untilDate: new Date(
                                `${draftPeriod.endDate}T12:00:00`
                            ),
                        }}
                        handleFromDateChange={handleCleaningFromDateChange}
                        handleUntilDateChange={handleCleaningUntilDateChange}
                        reloadData={applyPeriod}
                        shortcuts={{
                            active: currShortcut,
                            list: shortcut_list,
                        }}
                        handleShortcutClick={shortcutId =>
                            handleCleaningPeriodShortcut(shortcutId)
                        }
                        exportButton={
                            <ExportCSVButton
                                label={
                                    <LocaleMessage msg="button.export_data" />
                                }
                                data={csvData}
                                headers={csvHeaders}
                                filename={GetFileName(
                                    'REPORT-cleaning-fleet',
                                    'csv'
                                )}
                                disabled={csvData.length === 0}
                            />
                        }
                    />
                    <div className="sidecard-body mt-3">
                        {total === 0 ? (
                            <div className="jr-card text-center p-5 mb-5 mt-3 w-100">
                                <Typography variant="h6" color="textSecondary">
                                    {(() => {
                                        if (selectedCc1RobotIds.length === 0)
                                            return (
                                                <LocaleMessage msg="page.robots.cleaning_report.no_robots_selected" />
                                            );
                                        if (
                                            loadError ||
                                            (reportPayload &&
                                                reportPayload.apiError)
                                        )
                                            return (
                                                <LocaleMessage msg="page.robots.cleaning_report.error_contact_support" />
                                            );
                                        return (
                                            <LocaleMessage msg="page.robots.cleaning_report.no_data_check_wifi" />
                                        );
                                    })()}
                                </Typography>
                            </div>
                        ) : (
                            <div
                                className="animated slideInUpTiny animation-duration-3 mb-5"
                                style={{ width: '100%' }}
                            >
                                <div className="row card-row mb-5">
                                    <div className="col-md-3 col-12 dash-card mb-3">
                                        <InfoCard
                                            data={{
                                                title: String(total),
                                                subTitle: (
                                                    <LocaleMessage msg="page.robots.cleaning_report.card_total_records" />
                                                ),
                                            }}
                                            color={cardColor}
                                            icon="format-list-bulleted"
                                            onClick={scrollToCleaningTable}
                                        />
                                    </div>
                                    <div className="col-md-3 col-12 dash-card mb-3">
                                        <InfoCard
                                            data={{
                                                title: totalCleanFormatted,
                                                subTitle: (
                                                    <LocaleMessage msg="page.robots.cleaning_report.card_clean_time" />
                                                ),
                                            }}
                                            color={cardColor}
                                            icon="timer"
                                        />
                                    </div>
                                    <div className="col-md-3 col-12 dash-card mb-3">
                                        <InfoCard
                                            data={{
                                                title: `${totalArea} m²`,
                                                subTitle: (
                                                    <LocaleMessage msg="page.robots.cleaning_report.card_clean_area" />
                                                ),
                                            }}
                                            color={cardColor}
                                            icon="border-style"
                                        />
                                    </div>
                                    <div className="col-md-3 col-12 dash-card mb-3">
                                        <InfoCard
                                            data={{
                                                title: String(
                                                    displayRobotsQueried
                                                ),
                                                subTitle: (
                                                    <LocaleMessage msg="page.robots.cleaning_report.live.robots_queried" />
                                                ),
                                            }}
                                            color={cardColor}
                                            icon="android"
                                        />
                                    </div>
                                    <EfficiencyInfoCard
                                        list={list}
                                        color={cardColor}
                                    />
                                    <CleaningReportUtilityInfoCards
                                        consumption={
                                            reportPayload &&
                                            reportPayload.consumption
                                        }
                                        cardColor={cardColor}
                                        robotDilutions={robotDilutions}
                                        list={list}
                                    />
                                </div>

                                <CleaningReportROICard
                                    totalArea={totalArea}
                                    cardColor={cardColor}
                                    chartColor={graphColor}
                                    list={list}
                                    humanProd={humanProd}
                                    periodDates={periodDates}
                                />

                                <CleaningReportUtilityCharts
                                    list={list}
                                    periodDates={periodDates}
                                    localeCode={localeCode}
                                    consumption={
                                        reportPayload &&
                                        reportPayload.consumption
                                    }
                                />

                                <CleaningReportBatteryDashboard
                                    list={list}
                                    localeCode={localeCode}
                                    periodDates={periodDates}
                                />

                                <div className="row card-row">
                                    <CleaningReportProductSpentCard
                                        list={list}
                                        localeCode={localeCode}
                                        consumption={
                                            reportPayload &&
                                            reportPayload.consumption
                                        }
                                        robotDilutions={robotDilutions}
                                        periodDates={periodDates}
                                    />
                                    <CleaningReportBatteryByModeCard
                                        list={list}
                                        localeCode={localeCode}
                                    />
                                    {/* <CleaningReportFleetScheduleCard
                                        list={list}
                                        localeCode={localeCode}
                                    /> */}
                                </div>

                                {isAdmin && isAllGroupsSelected && (
                                    <>
                                        {/* TODO: DEPRECATED - O GroupComparisonDashboardCard original foi substituído pelo EfficiencyGroupComparisonCard para validação do cliente. Revalidar após feedback e remover o código duplicado.
                                        <GroupComparisonDashboardCard
                                            data={groupComparisonData}
                                            cardColor={graphColor}
                                        /> */}
                                        <EfficiencyGroupComparisonCard
                                            data={groupComparisonData}
                                            cardColor={graphColor}
                                        />
                                    </>
                                )}

                                {robotComparisonData &&
                                    robotComparisonData.length > 1 && (
                                        <>
                                            {/* TODO: DEPRECATED - O RobotComparisonDashboardCard original foi substituído pelo EfficiencyRobotComparisonCard para validação do cliente. Revalidar após feedback e remover o código duplicado.
                                            <RobotComparisonDashboardCard
                                                data={robotComparisonData}
                                                cardColor={graphColor}
                                            /> */}
                                            <EfficiencyRobotComparisonCard
                                                data={robotComparisonData}
                                                cardColor={graphColor}
                                                robotDilutions={robotDilutions}
                                                list={list}
                                            />
                                        </>
                                    )}

                                <EfficiencyGraphCard
                                    localeCode={localeCode}
                                    periodDates={periodDates}
                                    list={list}
                                    color={graphColor}
                                />

                                <CleaningReportChartsCard
                                    cardColor={graphColor}
                                    chartColor={cardColor}
                                    pieSegmentColors={pieSegmentColors}
                                    list={list}
                                    localeCode={localeCode}
                                    periodDates={periodDates}
                                />

                                <CleaningReportAreaComparisonCard
                                    primaryColor={graphColor}
                                    secondaryColor={cardColor}
                                    data={areaCompareSeries}
                                />

                                <CleaningReportAreaByHourCard
                                    barColor={graphColor}
                                    data={areaByHourSeries}
                                />

                                <div className="row card-row">
                                    <div
                                        ref={cleaningTableRef}
                                        id="cleaning-report-table-pluginspace"
                                        className="col-12 mb-5 dash-card"
                                    >
                                        <div className="jr-card jr-card-widget p-3">
                                            <Typography
                                                variant="h6"
                                                className="mb-3"
                                            >
                                                <LocaleMessage msg="page.robots.cleaning_report.live.table_title" />
                                            </Typography>
                                            <div className="table-responsive">
                                                <table className="table table-striped mb-0">
                                                    <CleaningTaskTableHead />
                                                    <tbody>
                                                        <CleaningTaskTableRows
                                                            rows={
                                                                tableRowsForDisplay
                                                            }
                                                            localeCode={
                                                                localeCode
                                                            }
                                                            onRowClick={
                                                                onRowClick
                                                            }
                                                        />
                                                    </tbody>
                                                </table>
                                                <CleaningReportTasksTablePagination
                                                    count={tasksTableTotalCount}
                                                    page={tasksTablePage}
                                                    rowsPerPage={
                                                        tasksTableRowsPerPage
                                                    }
                                                    onPageChange={
                                                        onTasksTablePageChange
                                                    }
                                                    onRowsPerPageChange={
                                                        onTasksTableRowsPerPageChange
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </CardSideBordered>
        </div>
    );
}

CleaningReportPluginspaceMainCard.propTypes = {
    cc1Robots: PropTypes.arrayOf(cc1RobotPropType).isRequired,
    cc1RobotsLoading: PropTypes.bool.isRequired,
    selectedCc1RobotIds: PropTypes.arrayOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ).isRequired,
    onCc1RobotFilterChange: PropTypes.func.isRequired,
    onCc1RobotFilterConfirm: PropTypes.func.isRequired,
    date_loc: dateFnsLocalePropType.isRequired,
    draftPeriod: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }).isRequired,
    handleCleaningFromDateChange: PropTypes.func.isRequired,
    handleCleaningUntilDateChange: PropTypes.func.isRequired,
    applyPeriod: PropTypes.func.isRequired,
    currShortcut: PropTypes.string.isRequired,
    shortcut_list: shortcutListPropType.isRequired,
    handleCleaningPeriodShortcut: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired,
    totalCleanFormatted: PropTypes.string.isRequired,
    totalArea: PropTypes.string.isRequired,
    robotsQueried: PropTypes.number.isRequired,
    cardColor: PropTypes.string.isRequired,
    graphColor: PropTypes.string.isRequired,
    pieSegmentColors: PropTypes.arrayOf(PropTypes.string).isRequired,
    list: PropTypes.arrayOf(cleaningReportRowPropType).isRequired,
    localeCode: PropTypes.string.isRequired,
    periodDates: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }).isRequired,
    areaCompareSeries: PropTypes.arrayOf(areaComparePointPropType).isRequired,
    areaByHourSeries: PropTypes.arrayOf(areaByHourPointPropType).isRequired,
    scrollToCleaningTable: PropTypes.func.isRequired,
    cleaningTableRef: cleaningTableRefPropType.isRequired,
    tableRowsForDisplay: PropTypes.arrayOf(cleaningReportRowPropType)
        .isRequired,
    onRowClick: PropTypes.func,
    tasksTablePage: PropTypes.number.isRequired,
    tasksTableRowsPerPage: PropTypes.number.isRequired,
    tasksTableTotalCount: PropTypes.number.isRequired,
    onTasksTablePageChange: PropTypes.func.isRequired,
    onTasksTableRowsPerPageChange: PropTypes.func.isRequired,
    humanProd: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reportPayload: PropTypes.shape({}),
    loadError: PropTypes.shape({}),
    robotDilutions: PropTypes.arrayOf(
        PropTypes.shape({
            robot_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            sn: PropTypes.string,
            dilution_ml_per_l: PropTypes.number,
        })
    ),
};

CleaningReportPluginspaceMainCard.defaultProps = {
    onRowClick: undefined,
    humanProd: 150,
    reportPayload: {},
    loadError: null,
    robotDilutions: [],
};

/**
 * NOTA DE ARQUITETURA (i18n):
 * O `export default` está localizado aqui utilizando o HOC `injectIntl` pois
 * o projeto utiliza a versão 2.9.0 do `react-intl`. Nesta versão, o hook `useIntl()`
 * não está disponível (foi introduzido apenas na v3+). Portanto, a única maneira
 * de acessar a instância `intl` para repassar à lógica de gráficos (Group Comparison)
 * é englobando o componente na função `injectIntl`.
 */
export default injectIntl(CleaningReportPluginspaceMainCard);
